import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { fileConfig } from '@adornis/file-utils/config.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import AdmZip from 'adm-zip';
import type fileUpload from 'express-fileupload';
import { cpSync, readdirSync, rmSync } from 'fs';
import path from 'path';

export class EbookFile extends AdornisFile {
  static override _class = 'EbookFile';
  static override customUpload = true;

  static override async customUploadHandler(
    additionalInfo: Record<string, string>,
    retrievedFiles: fileUpload.FileArray,
    fileID?: string,
  ): Promise<AdornisFile> {
    super.customUpload;
    const file = retrievedFiles;
    const filePath = file._file.tempFilePath;
    logger.error({ foo: 'bar', file, filePath }, 'upload files not implemented');
    const fileName = file._file.name.split('.')[0];
    const outputDirTemp = path.join(fileConfig.get('datadir'), 'epub', fileName + '_temp');
    const outputDirFinal = path.join(fileConfig.get('datadir'), 'epub', fileName);

    try {
      const zip = new AdmZip(filePath);

      // TODO: fail on already uploaded
      zip.extractAllTo(outputDirTemp, false);
      cpSync(path.join(outputDirTemp, 'OEBPS'), outputDirFinal, { recursive: true });
      rmSync(outputDirTemp, { recursive: true });
      console.log(`Successfully unzipped to: ${outputDirFinal}`);
    } catch (e) {
      console.error(`Error unzipping file: ${e}`);
    }

    return new AdornisFile({ _id: 'does_not_really exist' });
  }
}

@Entity()
export class EbookEntry extends AdornisEntity {
  static override _class = 'EbookEntry';

  @Field(() => String)
  public key!: string;

  @Field(() => String)
  public title!: string;

  @Field(() => String)
  public description!: string;

  @Field(() => String)
  public coverImage: Maybe<string>;
}

export const getAvailableEbooks = registerQuery({
  operationName: 'getAvailableEbooks',
  type: () => [EbookEntry],
  resolve: () => {
    return async (gqlFields: BaseQLSelectionSet<EbookEntry>) => {
      try {
        const ebookPath = path.join(fileConfig.get('datadir'), 'epub');

        const { existsSync, mkdirSync } = await import('fs');
        if (!existsSync(ebookPath)) {
          mkdirSync(ebookPath, { recursive: true });
        }
        const dirs = readdirSync(ebookPath);

        const result = [] as EbookEntry[];
        for (const dir of dirs) {
          const coverImage = (() => {
            const images = readdirSync(path.join(ebookPath, dir, 'image')).filter(f => f.endsWith('.png'));
            const coverImageName = images.find(f => f.toLowerCase().includes('cover'));
            return coverImageName ? `/epub/${dir}/image/${coverImageName}` : null;
          })();

          result.push(
            new EbookEntry({
              key: dir,
              title: dir,
              description: dir,
              coverImage,
            }),
          );
        }

        return result;
      } catch (e) {
        console.error(e);
        return [];
      }
    };
  },
});
